import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { WEBPROFIT_FREE_WEBSITE_URL } from "../utils/webProfitPromotion";

const outboundProps = {
  href: WEBPROFIT_FREE_WEBSITE_URL,
  target: "_blank",
  rel: "noopener noreferrer",
};

/** Franja independiente del diseño configurable del negocio. */
export function WebProfitPromoBanner({ onHeightChange }) {
  const bannerRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (typeof onHeightChange !== "function" || !bannerRef.current) return undefined;
    const element = bannerRef.current;
    const updateHeight = () => onHeightChange(Math.ceil(element.getBoundingClientRect().height));
    updateHeight();
    // No suponemos una altura móvil fija: el texto puede ocupar dos líneas.
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [onHeightChange]);

  return (
    <Box
      ref={bannerRef}
      component="aside"
      aria-label="Creá tu página con WebProfit"
      data-webprofit-promotion="banner"
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0.65, sm: 2 },
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1, sm: 0.85 },
        minHeight: { xs: 78, sm: 50 },
        bgcolor: "#19242F",
        color: "#FFFFFF",
        borderBottom: "1px solid rgba(255,255,255,.16)",
      }}
    >
      <Typography component="span" sx={{ fontWeight: 800, fontSize: { xs: 13, sm: 14 }, textAlign: "center", lineHeight: 1.35 }}>
        Creá tu página web gratis con WebProfit
      </Typography>
      <Button
        component="a"
        {...outboundProps}
        variant="contained"
        size="small"
        sx={{
          flexShrink: 0,
          minHeight: 34,
          px: 1.8,
          borderRadius: 1.5,
          bgcolor: "#F35A3A",
          color: "#FFFFFF",
          fontSize: 12,
          fontWeight: 800,
          lineHeight: 1.15,
          textTransform: "none",
          "&:hover": { bgcolor: "#D74225" },
          "&:focus-visible": { outline: "3px solid #FFFFFF", outlineOffset: 2 },
        }}
      >
        Crear mi página gratis
      </Button>
    </Box>
  );
}

/** Sello obligatorio fuera del Footer configurable, incluso si está oculto. */
export function WebProfitPromoFooter({ isTrial = false }) {
  return (
    <Box
      component="aside"
      aria-label="WebProfit"
      data-webprofit-promotion="footer"
      sx={{
        width: "100%", px: 2, py: 1.25, textAlign: "center",
        bgcolor: "#0f1115", color: "rgba(255,255,255,.82)",
        borderTop: "1px solid rgba(255,255,255,.10)",
      }}
    >
      <Typography
        component="a"
        {...outboundProps}
        sx={{
          fontSize: { xs: 12, sm: 13 }, fontWeight: 700,
          color: "inherit", textDecoration: "none",
          "&:hover": { color: "#FFFFFF", textDecoration: "underline" },
          "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: 3 },
        }}
      >
        {isTrial ? "Creado gratis con WebProfit" : "Sitio creado con WebProfit"}
      </Typography>
    </Box>
  );
}

/** Sólo se monta en pantallas de indisponibilidad con billing confirmado. */
export function WebProfitPromoUnavailable() {
  return (
    <Box component="aside" data-webprofit-promotion="unavailable" sx={{ mt: 4, mx: "auto", maxWidth: 470, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: ".02em", color: "#F35A3A" }}>
        WebProfit
      </Typography>
      <Typography sx={{ mt: 1, fontWeight: 750, fontSize: { xs: 17, sm: 19 }, color: "text.primary" }}>
        Creá tu página web gratis con WebProfit
      </Typography>
      <Typography sx={{ mt: 0.6, mb: 2, color: "text.secondary", fontSize: 14 }}>
        Vos también podés tener tu propia página web.
      </Typography>
      <Button component="a" {...outboundProps} variant="contained" sx={{ bgcolor: "#F35A3A", color: "#FFFFFF", textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: "#D74225" } }}>
        Crear mi página gratis
      </Button>
    </Box>
  );
}
